import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import CustomerProofView from "@/components/mobile/CustomerProofView";
import { subscribeOrderPaymentUpdated } from "@/lib/orderPaymentWs";
import { CUSTOMER_PROOF_PAGE_CSS, FATHERS_DAY_OFFER_URL } from "@/lib/customerProofPageStyles";
import { appPathname } from "@/lib/opsBase";
import { usePollFreshness } from "@/lib/pollFreshness";
import PollStaleNotice from "@/components/PollStaleNotice";

function readProofCode() {
  const match = appPathname().match(/^\/p\/cake-proof\/([A-Za-z0-9]+)\/?$/i);
  return match?.[1] || "";
}

export default function CakeProofPage() {
  const code = useMemo(readProofCode, []);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [approving, setApproving] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const { isStale, lastSuccessMs, markSuccess, markFailure } = usePollFreshness();

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    const href = new URL(FATHERS_DAY_OFFER_URL, window.location.origin).href;
    if (!document.querySelector("link[data-fathers-day-offer='prefetch']")) {
      const link = document.createElement("link");
      link.rel = "prefetch";
      link.href = href;
      link.as = "document";
      link.setAttribute("data-fathers-day-offer", "prefetch");
      document.head.appendChild(link);
    }
    fetch(href, { cache: "force-cache" }).catch(() => {});
  }, []);

  const loadProof = useCallback(async () => {
    if (!code) {
      setErr("Invalid link.");
      return;
    }
    const r = await fetch(`/api/p/cake-proof/${encodeURIComponent(code)}`);
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      setErr(
        typeof data.detail === "string"
          ? data.detail
          : "This link is invalid or expired. Ask The Table Top to resend your cake photos on WhatsApp."
      );
      return;
    }
    setInfo(data);
    setErr("");
    markSuccess();
  }, [code, markSuccess]);

  useEffect(() => {
    let cancelled = false;
    loadProof().catch(() => {
      if (!cancelled) setErr("Could not load your cake. Check your connection and try again.");
    });
    return () => {
      cancelled = true;
    };
  }, [loadProof]);

  const trackingActive = Boolean(
    info?.delivery_tracking?.active || info?.delivery_tracking?.show_map
  );

  const timelineActive = Boolean(
    info?.production_timeline?.length &&
      !info?.production_timeline_summary?.complete &&
      info?.production_timeline_summary?.current_key !== "cancelled"
  );

  useEffect(() => {
    if (!code || !trackingActive) return undefined;
    let cancelled = false;

    const poll = async () => {
      try {
        const r = await fetch(`/api/p/cake-proof/${encodeURIComponent(code)}/tracking`);
        const data = await r.json().catch(() => ({}));
        if (cancelled || !r.ok) return;
        setInfo((prev) =>
          prev
            ? {
                ...prev,
                order_status: data.order_status ?? prev.order_status,
                delivery_tracking: data.delivery_tracking ?? prev.delivery_tracking,
              }
            : prev
        );
      } catch {
        /* ignore transient poll errors */
      }
    };

    poll();
    const id = window.setInterval(poll, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [code, trackingActive]);

  useEffect(() => {
    if (!code || !timelineActive) return undefined;
    const poll = () => {
      loadProof().catch(markFailure);
    };
    poll();
    const id = window.setInterval(poll, 8000);
    return () => window.clearInterval(id);
  }, [code, timelineActive, loadProof, markFailure]);

  const paymentPending = Boolean(info?.payment_due?.show);

  useEffect(() => {
    if (!code || !paymentPending) return undefined;
    const poll = () => {
      loadProof().catch(markFailure);
    };
    poll();
    const id = window.setInterval(poll, 5000);
    const onVisible = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [code, paymentPending, loadProof, markFailure]);

  useEffect(() => {
    const oid = info?.order_id;
    if (!oid) return undefined;
    return subscribeOrderPaymentUpdated({
      orderId: oid,
      onUpdate: () => loadProof(),
    });
  }, [info?.order_id, loadProof]);

  const approvalActive = Boolean(
    info?.proof_approval?.active &&
      !info?.proof_approval?.is_approved &&
      !info?.proof_approval?.is_released
  );

  useEffect(() => {
    if (!code || !approvalActive) return undefined;
    const poll = () => {
      loadProof().catch(markFailure);
    };
    poll();
    const id = window.setInterval(poll, 3000);
    return () => window.clearInterval(id);
  }, [code, approvalActive, loadProof, markFailure]);

  const onApprove = async () => {
    if (!code || approving) return;
    setApproving(true);
    try {
      const r = await fetch(`/api/p/cake-proof/${encodeURIComponent(code)}/approve`, {
        method: "POST",
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(
          typeof data.detail === "string"
            ? data.detail
            : "Could not confirm approval. Please call us if you need a change."
        );
        return;
      }
      setInfo(data);
      setErr("");
      if (!data?.service_review?.submitted) {
        setShowReviewPopup(true);
      }
    } catch {
      setErr("Could not confirm approval. Check your connection and try again.");
    } finally {
      setApproving(false);
    }
  };

  const onSubmitReview = async ({ rating, comment }) => {
    if (!code || submittingReview || rating < 1) return;
    setSubmittingReview(true);
    try {
      const r = await fetch(`/api/p/cake-proof/${encodeURIComponent(code)}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment: comment || "" }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(
          typeof data.detail === "string"
            ? data.detail
            : "Could not save your review. Please try again."
        );
        return;
      }
      setInfo(data);
      setErr("");
      setShowReviewPopup(false);
      window.location.assign(FATHERS_DAY_OFFER_URL);
    } catch {
      setErr("Could not save your review. Check your connection and try again.");
    } finally {
      setSubmittingReview(false);
    }
  };

  const pageCss = CUSTOMER_PROOF_PAGE_CSS;

  if (!info && !err) {
    return (
      <div className="customer-proof-page">
        <style>{pageCss}</style>
        <div className="customer-proof-loading">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: "#EE2677" }} />
          <p className="customer-proof-loading-text">Loading your cake…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-proof-page">
      <style>{pageCss}</style>
      <PollStaleNotice isStale={isStale} lastSuccessMs={lastSuccessMs} />
      {err ? (
        <div className="customer-proof-error">
          <p className="customer-proof-error-title">Link not available</p>
          <p className="customer-proof-error-text">{err}</p>
        </div>
      ) : (
        <CustomerProofView
          info={info}
          onApprove={onApprove}
          approving={approving}
          onSubmitReview={onSubmitReview}
          submittingReview={submittingReview}
          showReviewPopup={showReviewPopup}
          onCloseReview={() => setShowReviewPopup(false)}
        />
      )}
    </div>
  );
}
